// Phase 33 · 19a — the Dashboard overview: four figures, then the work.
//
// Ranked by consequence rather than recency. The attention list is
// breached-and-breaching only, oldest deadline first, each row carrying the
// agent who holds it — and the foot states the finding rather than leaving it
// to be noticed, because "four of five sit with one agent" is a routing
// problem and reads as a performance one until someone says otherwise.
//
// The artboard's "Where the time goes" stage-dwell card is deliberately absent.
// Stage transitions exist as `ticket.stage_changed` history rows but there is
// no aggregate dwell endpoint, and phase 32 §1 is explicit that approximating
// it from `updated_at` is worse than showing nothing — a wrong bottleneck
// sends people to fix the wrong stage. Agent load takes the full rail until
// the aggregate exists.

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { inboxService } from '@services/index';
import type { InboxItem } from '@services/inbox.service';
import type { AgentMetrics, DashboardMetrics, SlaMetrics } from '@services/analytics.service';
import { duration } from '@shared/format/duration';
import { initials, identityColor, personName } from '@components/tickets/identity';
import { bySlaThenReceived, slaLabel, slaTone, type SlaTone } from '../inbox/inboxQueue';
import {
  agentLoad,
  breachedTickets,
  concentration,
  isNotable,
  median,
  msToSeconds,
  openCount,
} from './overviewData';

/** The floating shell: cards sit ON the canvas, they do not butt against it. */
const CARD: React.CSSProperties = { borderRadius: 10, boxShadow: 'var(--co-shadow-card)' };
const CARD_CLASS = 'border border-[var(--co-line)] bg-[var(--co-raised)]';

const TONE_INK: Record<SlaTone, string> = {
  danger: 'var(--co-burgundy)',
  warn: 'var(--co-terracotta-ink)',
  normal: 'var(--co-ink-2)',
  none: 'var(--co-ink-3)',
};

const pct = (v?: number | null) => (v === null || v === undefined ? '—' : `${v.toFixed(1)}%`);
const num = (v?: number | null) => (v === null || v === undefined ? '—' : String(v));

// ---------------------------------------------------------------- stat cards

function StatCard({
  value,
  label,
  sub,
  tone,
}: {
  value: string;
  label: string;
  sub: string;
  tone?: string;
}) {
  return (
    <div className={clsx(CARD_CLASS, 'min-w-[200px] flex-1 px-[18px] py-4')} style={CARD}>
      <div
        className="co-mono text-[27px] font-semibold leading-none"
        style={{ color: tone || 'var(--co-ink)', fontVariantNumeric: 'tabular-nums' }}
      >
        {value}
      </div>
      <div className="co-mono mt-2 text-[9.5px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">
        {label}
      </div>
      <div className="mt-[7px] border-t border-[var(--co-line-subtle)] pt-[9px] text-[12px] text-[var(--co-ink-3)]">
        {sub}
      </div>
    </div>
  );
}

export function StatRow({ metrics, sla }: { metrics?: DashboardMetrics; sla?: SlaMetrics }) {
  const t = metrics?.tickets;
  const open = openCount(t);
  const breached = breachedTickets(sla);
  const firstReply = msToSeconds(metrics?.performance?.avgFirstResponseTime);
  const resolution = msToSeconds(metrics?.performance?.avgResolutionTime);

  return (
    <div className="flex flex-none flex-wrap gap-[14px]">
      <StatCard
        value={num(open)}
        label="Open tickets"
        sub={
          t
            ? `${t.new || 0} new · ${t.in_progress || 0} in progress · ${t.pending || 0} pending`
            : 'Breakdown unavailable'
        }
      />
      <StatCard
        value={num(breached)}
        label="SLA breached"
        tone={breached ? 'var(--co-burgundy)' : undefined}
        sub={
          sla
            ? `${sla.firstResponse.breached} first response · ${sla.resolution.breached} resolution`
            : 'Dimensions unavailable'
        }
      />
      <StatCard
        value={duration(firstReply)}
        label="Avg first reply"
        tone={firstReply ? 'var(--co-green)' : undefined}
        // The BFF reports a mean, not a median — the artboard's word would be a
        // claim about a statistic nothing here computes.
        sub={resolution ? `Avg resolution ${duration(resolution)}` : 'No resolutions yet'}
      />
      <StatCard
        value={pct(metrics?.performance?.firstResponseSlaCompliance)}
        label="SLA compliance"
        sub={`Resolution dimension ${pct(metrics?.performance?.resolutionSlaCompliance)}`}
      />
    </div>
  );
}

// ----------------------------------------------------------- attention queue

/**
 * Breached or breaching within the hour, oldest deadline first.
 *
 * One page of the queue the Inbox already orders server-side
 * (`sortBy: 'sla_priority'`), filtered to the two tones that mean "now". A lead
 * sees the org; an agent has no 'all' view, so they see their own.
 */
function useAttention(isLead: boolean) {
  return useQuery({
    queryKey: ['dashboard', 'attention', isLead ? 'all' : 'mine'] as const,
    queryFn: () =>
      inboxService.getInbox({
        view: isLead ? 'all' : 'assigned_to_me',
        limit: 25,
        page: 1,
      }),
    staleTime: 30_000,
    select: (res) => {
      const now = Date.now();
      return (res.data || [])
        .filter((i) => {
          const tone = slaTone(i, now);
          return tone === 'danger' || tone === 'warn';
        })
        .sort(bySlaThenReceived);
    },
  });
}

function AttentionRow({ item, onOpen }: { item: InboxItem; onOpen: (id: string) => void }) {
  const tone = slaTone(item);
  const name = item.assignee?.name;
  return (
    <button
      type="button"
      onClick={() => onOpen(item.id)}
      className="flex w-full items-center gap-[14px] border-b border-[var(--co-line-subtle)] px-5 py-3 text-left last:border-b-0 hover:bg-[var(--co-selected)]"
      style={{ borderLeft: `2px solid ${TONE_INK[tone]}` }}
    >
      <div className="min-w-0 flex-1">
        <div className="truncate text-[13.5px] font-semibold text-[var(--co-ink)]">
          {item.subject || 'Untitled'}
        </div>
        <div className="co-mono mt-1 truncate text-[10.5px] text-[var(--co-ink-3)]">
          {item.ticketNumber ? `${item.ticketNumber} · ` : ''}
          {item.customer?.name || item.customer?.email || 'Unknown sender'}
        </div>
      </div>

      <div className="flex w-[150px] flex-none items-center gap-2">
        <span
          className="flex h-7 w-7 flex-none items-center justify-center rounded-[7px] text-[11px] font-semibold text-[var(--co-ink-invert)]"
          style={{ background: name ? identityColor(name) : 'var(--co-line-strong)' }}
          aria-hidden
        >
          {name ? initials(name) : '·'}
        </span>
        <span
          className={clsx(
            'truncate text-[12.5px]',
            name ? 'text-[var(--co-ink-2)]' : 'italic text-[var(--co-ink-3)]'
          )}
        >
          {personName(name) || 'Unassigned'}
        </span>
      </div>

      <div
        className="co-mono w-[104px] flex-none text-right text-[11.5px] font-semibold"
        style={{ color: TONE_INK[tone] }}
      >
        {slaLabel(item)}
      </div>
    </button>
  );
}

export function AttentionList({ isLead }: { isLead: boolean }) {
  const navigate = useNavigate();
  const { data: rows = [], isLoading, isError } = useAttention(isLead);

  const shown = rows.slice(0, 6);
  const conc = useMemo(() => concentration(rows), [rows]);

  return (
    <section
      className={clsx(CARD_CLASS, 'flex min-w-0 flex-1 flex-col overflow-hidden')}
      style={CARD}
    >
      <div className="flex flex-none items-center gap-3 border-b border-[var(--co-line-subtle)] px-5 pb-[14px] pt-4">
        <div className="min-w-0">
          <h2 className="text-[20px] font-bold leading-[1.15] tracking-[-.018em] text-[var(--co-ink)]">
            Needs attention now
          </h2>
          <p className="mt-1 text-[12.5px] text-[var(--co-ink-3)]">
            Breached or breaching within the hour, oldest first.
          </p>
        </div>
        {/* No breach count here: this card reads one page of the queue, so any
            total it printed was capped at the page size and contradicted the
            SLA breached figure above, which is the one count. */}
      </div>

      <div className="min-h-0 flex-1">
        {isLoading ? (
          <div className="space-y-3 px-5 py-5">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-9 animate-pulse rounded bg-[var(--co-sunken)]" />
            ))}
          </div>
        ) : isError ? (
          <p className="px-5 py-12 text-[13px] text-[var(--co-ink-3)]">
            The queue could not be loaded, so nothing here is a statement about your SLAs.
          </p>
        ) : shown.length === 0 ? (
          <p className="px-5 py-12 text-[13px] text-[var(--co-ink-3)]">
            Nothing breached and nothing due within the hour.
          </p>
        ) : (
          shown.map((i) => (
            <AttentionRow key={i.id} item={i} onOpen={(id) => navigate(`/tickets/${id}`)} />
          ))
        )}
      </div>

      <div
        className="flex flex-none flex-wrap items-center gap-[14px] border-t border-[var(--co-line)] bg-[var(--co-selected)] px-5 py-3"
        style={{ borderRadius: '0 0 9px 9px' }}
      >
        <span className="co-mono whitespace-nowrap text-[10.5px] uppercase tracking-[.1em] text-[var(--co-ink-3)]">
          {shown.length} most urgent shown
        </span>
        <div className="flex-1" />
        <span className="text-[12px] text-[var(--co-ink-3)]">
          {/* Who holds the work is a lead's finding; on an agent's own queue
              it is always the agent. */}
          {isLead && isNotable(conc) && conc
            ? conc.holder
              ? `${conc.held} of ${conc.total} sit with ${conc.holder} — a routing problem, not a performance one.`
              : `${conc.held} of ${conc.total} have no owner — nobody is late, because nobody is holding them.`
            : 'Sorted by deadline — the top row is the one that costs most, not the newest.'}
        </span>
      </div>
    </section>
  );
}

// --------------------------------------------------------------- agent load

export function AgentLoad({ agents, loading }: { agents?: AgentMetrics[]; loading: boolean }) {
  const rows = useMemo(() => agentLoad(agents), [agents]);
  const mid = useMemo(() => (rows ? median(rows.map((r) => r.held)) : null), [rows]);
  const max = rows && rows.length ? Math.max(...rows.map((r) => r.held), 1) : 1;

  return (
    <section
      className={clsx(
        CARD_CLASS,
        'flex min-h-0 w-full flex-none flex-col overflow-hidden px-[18px] py-4 2xl:w-[420px]'
      )}
      style={CARD}
    >
      <div className="flex flex-none items-center gap-[10px]">
        <span className="co-mono text-[10px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">
          Agent load
        </span>
        <div className="flex-1" />
        {mid !== null && (
          <span className="co-mono text-[10px] text-[var(--co-ink-3)]">MEDIAN {mid}</span>
        )}
      </div>

      <div className="co-scrollbar-none mt-[14px] min-h-0 flex-1 overflow-y-auto">
        {loading ? (
          <div className="space-y-3">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-9 animate-pulse rounded bg-[var(--co-sunken)]" />
            ))}
          </div>
        ) : rows === null ? (
          <p className="text-[12.5px] leading-[1.55] text-[var(--co-ink-3)]">
            The workload snapshot did not load. The roster is here, but how much each person is
            holding is not — so no figure is shown rather than a column of zeros.
          </p>
        ) : rows.length === 0 ? (
          <p className="text-[12.5px] text-[var(--co-ink-3)]">No agents on this roster.</p>
        ) : (
          <div className="flex flex-col gap-3">
            {rows.map((r) => {
              const heavy = mid !== null && r.held > Math.max(mid * 2, mid + 2);
              return (
                <div key={r.id} className="flex items-center gap-[11px]">
                  <span
                    className="flex h-7 w-7 flex-none items-center justify-center rounded-[7px] text-[11px] font-semibold text-[var(--co-ink-invert)]"
                    style={{ background: identityColor(r.name) }}
                    aria-hidden
                  >
                    {initials(r.name)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[12.5px] font-semibold text-[var(--co-ink)]">
                      {personName(r.name) || r.name}
                    </div>
                    {r.role && (
                      <div className="co-mono mt-[2px] truncate text-[10px] uppercase tracking-[.08em] text-[var(--co-ink-3)]">
                        {r.role}
                      </div>
                    )}
                  </div>
                  <div className="w-24 flex-none">
                    <div className="h-[6px] overflow-hidden rounded-[3px] bg-[var(--co-sunken)]">
                      <div
                        className="h-full"
                        style={{
                          width: `${Math.round((r.held / max) * 100)}%`,
                          background: heavy ? 'var(--co-burgundy)' : 'var(--co-ink)',
                        }}
                      />
                    </div>
                    <div
                      className="co-mono mt-[5px] text-right text-[10.5px]"
                      style={{
                        color: heavy
                          ? 'var(--co-burgundy)'
                          : r.held === 0
                            ? 'var(--co-ink-3)'
                            : 'var(--co-ink-2)',
                      }}
                    >
                      {r.held} held
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <p className="mt-3 flex-none border-t border-[var(--co-line-subtle)] pt-3 text-[12px] leading-[1.5] text-[var(--co-ink-3)]">
        Only members holding tickets in this period are listed.
      </p>
    </section>
  );
}
